import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Bot, User, Code2, Copy, CheckCircle, List, Lightbulb } from 'lucide-react';
import botIcon from 'assets/images/course/chatbot_icon.svg';
import { sendChatMessage } from "ServerAPI/serverAPI";

const ChatComponent = ({ 
  problemStatement = "Sample problem statement", 
  codeSnippet = "print('Hello World')", 
  language = "python", 
  userId, 
  output 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);

  // Use userId to generate a consistent sessionId
  useEffect(() => {
    if (userId) {
      setSessionId(`session_${userId}`);
    }
  }, [userId]);

  // Reset messages when problemStatement changes
  useEffect(() => {
    setMessages([]);
  }, [problemStatement]);

  // Primary color scheme based on your requirements
  const colors = {
    primary: '#23479F',
    primaryHover: '#1e3c87',
    primaryActive: '#1c397f',
    background: '#ffffff',
    surface: '#f8f9fa',
    border: '#e9ecef',
    text: '#212529',
    textSecondary: '#6c757d',
    success: '#198754',
    light: '#f8f9fa',
    warning: '#ffc107',
    info: '#0dcaf0'
  };

  // Styles object
  const styles = {
    floatingButton: {
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      backgroundColor: colors.background,
      color: 'white',
      border: `2px solid ${colors.primary}`,
      borderRadius: '50%',
      width: '60px',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 8px 24px rgba(35, 71, 159, 0.3)',
      transition: 'all 0.3s ease',
      zIndex: 1001,
    },
    chatContainer: {
      position: 'fixed',
      bottom: '90px',
      right: '24px',
      width: '400px',
      height: '70vh',
      maxHeight: '600px',
      backgroundColor: colors.background,
      borderRadius: '16px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: 1050, 
      border: `1px solid ${colors.border}`
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
      color: 'white',
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
    },
    headerIcon: {
      width: '32px',
      height: '32px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    headerTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: '600'
    },
    headerSubtitle: {
      margin: 0,
      fontSize: '12px',
      opacity: 0.9
    },
    closeButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      borderRadius: '50%',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s'
    },
    messagesArea: {
      flexGrow: 1,
      padding: '20px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      backgroundColor: colors.surface
    },
    welcomeContainer: {
      textAlign: 'center',
      marginTop: 'auto'
    },
    welcomeIcon: {
      width: '60px',
      height: '60px',
      backgroundColor: colors.primary,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      opacity: 0.1
    },
    welcomeText: {
      color: colors.textSecondary,
      fontSize: '14px',
      marginBottom: '20px',
      fontWeight: '500'
    },
    suggestedQuestionsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    suggestedQuestion: {
      backgroundColor: colors.background,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '12px 16px',
      fontSize: '13px',
      cursor: 'pointer',
      textAlign: 'left',
      color: colors.text,
      transition: 'all 0.2s ease',
      fontWeight: '500'
    },
    inputArea: {
      display: 'flex',
      padding: '16px 20px',
      backgroundColor: colors.background,
      borderTop: `1px solid ${colors.border}`,
      gap: '12px',
      alignItems: 'flex-end'
    },
    messageInput: {
      flexGrow: 1,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '12px 16px',
      fontSize: '14px',
      outline: 'none',
      backgroundColor: colors.surface,
      transition: 'border-color 0.2s',
      resize: 'none',
      minHeight: '20px',
      maxHeight: '100px',
      fontFamily: 'inherit'
    },
    sendButton: {
      backgroundColor: colors.primary,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      width: '44px',
      height: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    // Enhanced content styles
    stepsContainer: {
      margin: '12px 0',
      padding: '0',
      listStyle: 'none'
    },
    stepItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px',
      padding: '12px',
      backgroundColor: colors.surface,
      borderRadius: '8px',
      border: `1px solid ${colors.border}`
    },
    stepNumber: {
      minWidth: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: colors.primary,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    stepContent: {
      fontSize: '14px',
      lineHeight: '1.5',
      flex: 1
    },
    inlineCode: {
      backgroundColor: '#f1f3f4',
      color: '#d73a49',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      border: '1px solid #e1e4e8'
    },
    codeBlock: {
      backgroundColor: '#f8f9fa',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      margin: '12px 0',
      overflow: 'hidden'
    },
    codeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      backgroundColor: colors.surface,
      borderBottom: `1px solid ${colors.border}`,
      fontSize: '12px',
      color: colors.textSecondary
    },
    copyButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 4px',
      borderRadius: '4px'
    },
    codeContent: {
      margin: 0,
      padding: '12px',
      fontSize: '13px',
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      overflow: 'auto',
      backgroundColor: '#ffffff',
      lineHeight: '1.4'
    },
    contentSection: {
      margin: '8px 0'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
      color: colors.primary,
      fontSize: '14px',
      fontWeight: '600'
    }
  };

  // Suggested questions for an empty chat
  const suggestedQuestions = [
    "Explain this problem to me.",
    "Give me a hint for this problem.",
    "What is the expected output for the given test cases?",
    "Check for syntax errors in my code.",
    "Help me optimize my code.",
  ];

  // Effect to handle clicking outside the chat window to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatWindowRef.current && !chatWindowRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Auto-scroll to the bottom of the chat on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Function to handle textarea auto-resize
  const handleTextareaResize = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';
  };

  // Enhanced function to detect and render different content types
  const renderEnhancedContent = (text) => {
    const bulletPattern = /^-\s+/gm;
    const numberedPattern = /^\d+\.\s+/gm;
    const codePattern = /```(\w+)?\n([\s\S]*?)```/g;
    
    let processedText = text;
    const codeBlocks = [];
    let codeMatch;
    
    while ((codeMatch = codePattern.exec(text)) !== null) {
      const codeId = `CODE_BLOCK_${codeBlocks.length}`;
      codeBlocks.push({
        id: codeId,
        language: codeMatch[1] || 'text',
        content: codeMatch[2].trim()
      });
      processedText = processedText.replace(codeMatch[0], `\n${codeId}\n`);
    }
    
    const hasBullets = bulletPattern.test(text);
    const hasNumbers = numberedPattern.test(text);
    
    if (hasBullets || hasNumbers) {
      return renderListContent(processedText, codeBlocks, hasBullets);
    }
    
    return renderEnhancedParagraph(processedText, codeBlocks);
  };

  // Render list content (both bulleted and numbered)
  const renderListContent = (text, codeBlocks, isBulleted = true) => {
    const splitPattern = isBulleted ? /^-\s+/gm : /^\d+\.\s+/gm;
    const items = text.split(splitPattern).filter(item => item.trim());
    
    return (
      <div>
        <div style={styles.sectionHeader}>
          <List size={16} />
          {isBulleted ? 'Key Points:' : 'Steps:'}
        </div>
        <ul style={styles.stepsContainer}>
          {items.map((item, index) => {
            const hasCodeBlock = codeBlocks.some(block => item.includes(block.id));
            
            return (
              <li key={index} style={styles.stepItem}>
                <div style={styles.stepNumber}>
                  {isBulleted ? '•' : index + 1}
                </div>
                <div style={styles.stepContent}>
                  {hasCodeBlock ? renderContentWithCodeBlocks(item, codeBlocks) : renderInlineCode(item)}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Render enhanced paragraph content
  const renderEnhancedParagraph = (text, codeBlocks) => {
    if (codeBlocks.length > 0) {
      return renderContentWithCodeBlocks(text, codeBlocks);
    }
    return renderInlineCode(text);
  };

  // Render content with code blocks
  const renderContentWithCodeBlocks = (text, codeBlocks) => {
    const parts = text.split(/\n(CODE_BLOCK_\d+)\n/);
    
    return (
      <div>
        {parts.map((part, index) => {
          const codeBlock = codeBlocks.find(block => block.id === part);
          
          if (codeBlock) {
            return (
              <div key={index} style={styles.codeBlock}>
                <div style={styles.codeHeader}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Code2 size={14} />
                    {codeBlock.language}
                  </span>
                  <button
                    onClick={() => copyToClipboard(codeBlock.content)}
                    style={styles.copyButton}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.border}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <Copy size={12} />
                    Copy
                  </button>
                </div>
                <pre style={styles.codeContent}>
                  <code>{codeBlock.content}</code>
                </pre>
              </div>
            );
          }
          
          return part.trim() ? (
            <div key={index} style={{ marginBottom: '8px' }}>
              {renderInlineCode(part)}
            </div>
          ) : null;
        })}
      </div>
    );
  };

  // Render text with inline code highlighting
  const renderInlineCode = (text) => {
    const inlineCodePattern = /`([^`]+)`/g;
    const parts = text.split(inlineCodePattern);
    
    return (
      <span>
        {parts.map((part, index) => {
          if (index % 2 === 1) {
            return (
              <code key={index} style={styles.inlineCode}>
                {part}
              </code>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </span>
    );
  };

  // Function to handle sending a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputMessage.trim() === '') return;

    const userMessage = { sender: 'user', text: inputMessage, timestamp: new Date() };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    const context = `Problem Statement: ${problemStatement}\n\nLanguage: ${language}\n\nCode Snippet:\n\`\`\`${language}\n${codeSnippet}\n\`\`\`\n\nUser Question: ${inputMessage}\n\nOutput of the code snippet: ${output}`;

    try {
      const response = await sendChatMessage(context, sessionId);
      
      // Since sessionId is now based on userId, we no longer update it from the response
      
      const aiMessage = { sender: 'ai', text: response.response, timestamp: new Date() };
      setMessages(prevMessages => [...prevMessages, aiMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = { sender: 'ai', text: 'Sorry, I am unable to connect to the AI service. Please try again later.', timestamp: new Date() };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSuggestedQuestionClick = (question) => {
    setInputMessage(question);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Chat Icon */}
      <button 
        style={styles.floatingButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open AI Assistant Chat"
      >
        <img 
          src={botIcon} 
          alt="AI Assistant"
          style={{ width: '40px', height: '40px', display: 'block' }}
        />
      </button>

      {/* Chat Interface */}
      {isOpen && (
        <div 
          ref={chatWindowRef} 
          style={styles.chatContainer}
        >
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerInfo}>
              <div style={styles.headerIcon}>
                <Bot size={20} />
              </div>
              <div>
                <h4 style={styles.headerTitle}>AI Assistant</h4>
                <p style={styles.headerSubtitle}>Always here to help</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              aria-label="Close Chat"
              style={styles.closeButton}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages Area */}
          <div style={styles.messagesArea}>
            {messages.length === 0 && (
              <div style={styles.welcomeContainer}>
                <div style={styles.welcomeIcon}>
                  <Bot size={30} color="white" />
                </div>
                <p style={styles.welcomeText}>
                  Hi! I'm here to help you with coding problems. What would you like to know?
                </p>
                <div style={styles.suggestedQuestionsContainer}>
                  {suggestedQuestions.map((q, index) => (
                    <button 
                      key={index} 
                      onClick={() => handleSuggestedQuestionClick(q)}
                      style={styles.suggestedQuestion}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = colors.primary;
                        e.target.style.color = 'white';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(35, 71, 159, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = colors.background;
                        e.target.style.color = colors.text;
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, index) => (
              <div 
                key={index} 
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: msg.sender === 'user' ? colors.primary : colors.success,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {msg.sender === 'user' ? 
                    <User size={16} color="white" /> : 
                    <Bot size={16} color="white" />
                  }
                </div>
                <div style={{
                  maxWidth: '80%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '16px',
                    backgroundColor: msg.sender === 'user' ? colors.primary : colors.background,
                    color: msg.sender === 'user' ? 'white' : colors.text,
                    border: msg.sender === 'user' ? 'none' : `1px solid ${colors.border}`,
                    boxShadow: msg.sender === 'user' ? 
                      '0 2px 8px rgba(35, 71, 159, 0.2)' : 
                      '0 1px 3px rgba(0, 0, 0, 0.1)',
                    borderBottomRightRadius: msg.sender === 'user' ? '6px' : '16px',
                    borderBottomLeftRadius: msg.sender === 'user' ? '16px' : '6px',
                  }}>
                    <div style={{ 
                      fontSize: '14px', 
                      lineHeight: '1.5',
                      wordWrap: 'break-word'
                    }}>
                      {msg.sender === 'ai' ? renderEnhancedContent(msg.text) : msg.text}
                    </div>
                  </div>
                  {msg.timestamp && (
                    <span style={{
                      fontSize: '11px',
                      color: colors.textSecondary,
                      alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                      marginTop: '2px'
                    }}>
                      {formatTime(msg.timestamp)}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: colors.success,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bot size={16} color="white" />
                </div>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '16px',
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.border}`,
                  borderBottomLeftRadius: '6px'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: colors.textSecondary,
                      animation: 'typing 1.4s infinite'
                    }} />
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: colors.textSecondary,
                      animation: 'typing 1.4s infinite 0.2s'
                    }} />
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: colors.textSecondary,
                      animation: 'typing 1.4s infinite 0.4s'
                    }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={styles.inputArea}>
            <textarea
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                handleTextareaResize(e);
              }}
              placeholder="Type your message... (Shift+Enter for new line)"
              style={styles.messageInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              rows={1}
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                handleSendMessage(e);
              }} 
              disabled={!inputMessage.trim() || isTyping}
              aria-label="Send message"
              style={{
                backgroundColor: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: inputMessage.trim() && !isTyping ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: inputMessage.trim() && !isTyping ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (inputMessage.trim() && !isTyping) {
                  e.target.style.backgroundColor = colors.primaryHover;
                  e.target.style.transform = 'scale(1.02)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.primary;
                e.target.style.transform = 'scale(1)';
              }}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.5;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default ChatComponent;